import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginSuccess, logout as logoutAction } from "../redux/authSlice";
import authService from "../services/AuthService";
import { RegistrationData } from "../models/RegistrationData";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationRef = useRef(false); // Prevent multiple initializations

  // Initialize auth service on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      // Prevent multiple initializations
      if (initializationRef.current) {
        return;
      }
      initializationRef.current = true;
      try {
        await authService.initialize();

        // Check if we have persisted Redux state but AuthService is not authenticated
        if (
          auth.isAuthenticated &&
          auth.token &&
          !authService.isAuthenticated
        ) {
          // Try to restore AuthService state from Redux
          try {
            authService.setToken(auth.token);
            authService.currentUser = auth.user;
            authService.isAuthenticated = true;

            // Validate the token with server (with fallback for development)
            if (process.env.NODE_ENV === "development") {
              authService.startTokenRefreshTimer();
            } else {
              try {
                const isValid = await authService.validateTokenWithServer();
                if (isValid) {
                  authService.startTokenRefreshTimer();
                } else {
                  // Token is invalid, clear everything
                  await authService.logout();
                  dispatch(logoutAction());
                }
              } catch (serverError) {
                // In development, if server validation fails, we'll trust the local state
                authService.startTokenRefreshTimer();
              }
            }
          } catch (error) {
            console.error("Error restoring auth state:", error);
            await authService.logout();
            dispatch(logoutAction());
          }
        }
        // Check if AuthService is authenticated but Redux is not
        else if (
          authService.isAuthenticated &&
          authService.currentUser &&
          !auth.isAuthenticated
        ) {
          dispatch(
            loginSuccess({
              user: authService.currentUser,
              role: authService.currentUser.role,
              token: authService.token,
            })
          );
        }
        // Check if both are authenticated but have different data
        else if (authService.isAuthenticated && auth.isAuthenticated) {
          // Ensure they are in sync - AuthService is the source of truth
          if (
            authService.token !== auth.token ||
            authService.currentUser?.id !== auth.user?.id
          ) {
            dispatch(
              loginSuccess({
                user: authService.currentUser,
                role: authService.currentUser.role,
                token: authService.token,
              })
            );
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        // Clear both states on error
        await authService.logout();
        dispatch(logoutAction());
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [dispatch]); // Only run once on mount

  const login = async (dni, password) => {
    setIsLoading(true);
    try {
      const result = await authService.login(dni, password);

      if (result.isSuccess) {
        dispatch(
          loginSuccess({
            user: result.user,
            role: result.user.role,
            token: result.token,
          })
        );
      }

      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (registrationData) => {
    setIsLoading(true);
    try {
      // Convert form data to RegistrationData model
      const regData = new RegistrationData({
        email: registrationData.correo,
        name: registrationData.nombre,
        lastName: registrationData.apellido,
        password: registrationData.password,
        dni: registrationData.dni,
        phone: registrationData.telefono,
        address: registrationData.direccion,
        postalCode: registrationData.municipio,
        city: "Isla Cristina", // Default city
        country: "España", // Default country
        necesidades: registrationData.necesidades.join(", "),
        observaciones: registrationData.observaciones || "",
      });

      const result = await authService.register(regData);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      dispatch(logoutAction());
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    setIsLoading(true);
    try {
      const result = await authService.changePassword(oldPassword, newPassword);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    setIsLoading(true);
    try {
      const result = await authService.forgotPassword(email);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const uploadCurriculum = async (file, userId) => {
    setIsLoading(true);
    try {
      const result = await authService.uploadCurriculum(file, userId);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const makeAuthenticatedRequest = async (config) => {
    return await authService.makeAuthenticatedRequest(config);
  };

  const value = {
    // Redux state
    user: auth.user,
    role: auth.role,
    isAuthenticated: auth.isAuthenticated,

    // Service state
    token: authService.token,
    isLoading,
    isInitialized,
    userVerified: authService.userVerified,

    // Methods
    login,
    register,
    logout,
    changePassword,
    forgotPassword,
    uploadCurriculum,
    makeAuthenticatedRequest,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


